import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthCtx, useCartCtx, useDarkMode } from '../../App';
import { useCurrency } from '../../hooks/useCurrency';
import { useRewardConfig } from '../../hooks/useRewardConfig';
import { useCategories } from '../../hooks/useCategories';
import CategoryIcon from '../CategoryIcon';
import { Icon } from '../ui/Icon';

// Shared couture nav-link styling: small uppercase, wide tracking, hairline
// underline that sweeps in on hover (see `.nav-link` in index.css).
const NAV_LINK =
  'nav-link text-[11px] uppercase tracking-[0.18em] font-medium text-gray-600 ' +
  'hover:text-tea-700 transition-colors dark:text-slate-300 dark:hover:text-tea-400';

// Catalogue dropdown — hover/focus-driven menu with the standard catalog plus
// the gamified Bulk Vault. Closes on blur or after a short hover-out delay.
function CatalogueMenu() {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const { categories } = useCategories();

  const show = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
  const hide = () => { closeTimer.current = setTimeout(() => setOpen(false), 120); };

  return (
    <div className="relative" onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        className={NAV_LINK + ' flex items-center gap-1.5'}
      >
        Catalogue
        <svg className={'w-3.5 h-3.5 transition-transform ' + (open ? 'rotate-180' : '')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-72 rounded-none border border-gray-200/80 bg-white/95 backdrop-blur-md shadow-[0_24px_60px_-20px_rgba(0,0,0,0.25)] py-3 z-50 dark:bg-slate-900/95 dark:border-slate-700"
          role="menu"
        >
          <Link
            to="/shop"
            onClick={() => setOpen(false)}
            className="block px-5 py-3 hover:bg-tea-50/70 dark:hover:bg-slate-800 transition-colors"
            role="menuitem"
          >
            <span className="block font-display text-lg tracking-wide text-gray-900 dark:text-slate-100">Shop All</span>
            <span className="block text-[11px] uppercase tracking-[0.15em] text-gray-400 dark:text-slate-400 mt-0.5">Every AB8 Beauty product</span>
          </Link>

          <div className="my-2 mx-5 border-t border-gray-100 dark:border-slate-700" />

          {categories.map(cat => (
            <Link
              key={cat.value}
              to={`/shop?category=${cat.value}`}
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-5 py-2.5 hover:bg-tea-50/70 dark:hover:bg-slate-800 transition-colors"
              role="menuitem"
            >
              <CategoryIcon category={cat.value} className="w-5 h-5 shrink-0 text-gray-500 dark:text-slate-400" />
              <span className="flex-1">
                <span className="block text-sm font-medium text-gray-900 dark:text-slate-100">{cat.label}</span>
                <span className="block text-[11px] text-gray-400 dark:text-slate-400 mt-0.5 line-clamp-1">{cat.blurb}</span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const { user, logout } = useAuthCtx();
  const { count } = useCartCtx();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { fmt } = useCurrency();
  const rewards = useRewardConfig();
  const { categories } = useCategories();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const openerRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate('/');
  };

  // Focus trap + Esc-to-close for the mobile drawer
  useEffect(() => {
    if (!menuOpen) return;
    const root = menuRef.current;
    const focusables = root?.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
    focusables?.[0]?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { setMenuOpen(false); openerRef.current?.focus(); }
      if (e.key === 'Tab' && focusables?.length) {
        const first = focusables[0], last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      {/* Promo banner — slim, understated plum bar with tracked caps */}
      <div
        className="text-pink-50/90 text-[10px] sm:text-[11px] font-medium uppercase tracking-[0.2em]"
        style={{ backgroundImage: 'linear-gradient(90deg, #3d0a23 0%, #5b0f33 50%, #3d0a23 100%)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-center text-center">
          <span className="hidden sm:inline">
            Complimentary shipping over {fmt(rewards.freeShippingThresholdCents)}
            {rewards.freeGiftEnabled && (
              <>
                <span className="mx-3 text-tea-300/60">&#9670;</span>
                A gift with every order over {fmt(rewards.mysteryGiftThresholdCents)}
              </>
            )}
          </span>
          <span className="sm:hidden">
            Free shipping over {fmt(rewards.freeShippingThresholdCents)}
            {rewards.freeGiftEnabled && <> · Gift over {fmt(rewards.mysteryGiftThresholdCents)}</>}
          </span>
        </div>
      </div>

      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200/70 dark:bg-slate-900/80 dark:border-slate-700/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo — AB8 wordmark */}
            <Link to="/" className="flex items-center shrink-0 group" aria-label="AB8 Beauty &amp; Skin — home">
              <img
                src="/logo-ab8.svg"
                alt="AB8 Beauty &amp; Skin"
                className="h-12 md:h-16 w-auto transition-transform group-hover:scale-105"
              />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-8">
              <CatalogueMenu />
              <Link to="/ambassadors" className={NAV_LINK}>Ambassadors</Link>
              <Link to="/press" className={NAV_LINK}>Press</Link>
              {user && (
                <Link to="/dashboard" className={NAV_LINK}>Dashboard</Link>
              )}
              {user?.role === 'affiliate' && (
                <Link to="/affiliate" className={NAV_LINK}>Affiliate</Link>
              )}
              {user?.role === 'admin' && (
                <>
                  <Link to="/admin/users" className={NAV_LINK}>Users</Link>
                  <Link to="/admin" className={NAV_LINK}>Admin</Link>
                </>
              )}
            </div>

            {/* Right section */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Referral cash — desktop only */}
              {user?.walletBalanceCents > 0 && (
                <Link
                  to="/account"
                  title="Referral cash — applies at checkout (excludes shipping)"
                  className="hidden lg:inline-flex items-center gap-1 rounded-full bg-tea-50 text-tea-700 text-xs font-semibold px-3 py-1.5 hover:bg-tea-100 transition-colors dark:bg-tea-900/30 dark:text-tea-400 dark:hover:bg-tea-900/50"
                >
                  <Icon name="commissions" className="w-4 h-4" />
                  {fmt(user.walletBalanceCents)}
                </Link>
              )}

              {/* Cart icon — always visible */}
              <Link to="/cart" className="relative p-2 text-gray-600 hover:text-tea-700 transition-colors dark:text-slate-300 dark:hover:text-tea-400" aria-label="Cart">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {count > 0 && (
                  <span className="absolute -top-1 -right-1 bg-tea-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {count}
                  </span>
                )}
              </Link>

              {/* Dark mode toggle — desktop only */}
              <button
                onClick={toggleDarkMode}
                className="hidden md:flex p-2 rounded-lg text-gray-500 hover:text-tea-700 hover:bg-gray-100 dark:text-slate-400 dark:hover:text-tea-400 dark:hover:bg-slate-800 transition-colors"
                aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M18.364 18.364l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Desktop: user name + logout OR login/signup */}
              {user ? (
                <div className="hidden md:flex items-center gap-4">
                  <Link to="/account" className={NAV_LINK + ' flex items-center gap-1.5'}>
                    {user.firstName}
                    {user.tier === 'platinum' && (
                      <span className="inline-block align-middle bg-slate-900 text-amber-300 text-[9px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 dark:bg-amber-900/40">
                        Platinum
                      </span>
                    )}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="inline-flex items-center justify-center rounded-full border border-gray-300 px-5 py-2 text-[11px] uppercase tracking-[0.15em] font-medium text-gray-700 hover:border-tea-600 hover:text-tea-700 transition-colors dark:border-slate-600 dark:text-slate-200 dark:hover:border-tea-500 dark:hover:text-tea-400"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="hidden md:flex items-center gap-2.5">
                  <Link
                    to="/login"
                    className="inline-flex items-center justify-center rounded-full border border-gray-300 px-5 py-2 text-[11px] uppercase tracking-[0.15em] font-medium text-gray-700 hover:border-tea-600 hover:text-tea-700 transition-colors dark:border-slate-600 dark:text-slate-200 dark:hover:border-tea-500 dark:hover:text-tea-400"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="inline-flex items-center justify-center rounded-full bg-tea-700 px-5 py-2 text-[11px] uppercase tracking-[0.15em] font-medium text-white hover:bg-tea-800 transition-colors"
                  >
                    Sign Up
                  </Link>
                </div>
              )}

              {/* Hamburger — mobile only */}
              <button
                ref={openerRef}
                onClick={() => setMenuOpen(true)}
                aria-label="Open menu"
                aria-expanded={menuOpen}
                className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" viewBox="0 0 24 24">
                  <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile drawer overlay */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <button
            aria-label="Close menu"
            className="absolute inset-0 bg-black/40"
            onClick={() => setMenuOpen(false)}
          />
          {/* Drawer panel */}
          <div
            ref={menuRef}
            className="relative ml-auto w-80 max-w-[85vw] h-full bg-white dark:bg-slate-900 shadow-xl flex flex-col overflow-y-auto"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700">
              <Link to="/" onClick={() => setMenuOpen(false)} className="flex items-center" aria-label="AB8 Beauty &amp; Skin — home">
                <img src="/logo-ab8.svg" alt="AB8 Beauty &amp; Skin" className="h-12 w-auto" />
              </Link>
              <button
                onClick={() => setMenuOpen(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-800"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Nav links */}
            <div className="flex-1 px-5 py-4 space-y-1">
              <Link to="/shop" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-tea-50 hover:text-tea-700 font-medium dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-tea-400 transition-colors">
                Shop All
              </Link>
              {categories.map(cat => (
                <Link key={cat.value} to={`/shop?category=${cat.value}`} onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 pl-6 pr-3 py-2.5 rounded-lg text-gray-600 hover:bg-tea-50 hover:text-tea-700 text-sm dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-tea-400 transition-colors">
                  <CategoryIcon category={cat.value} className="w-5 h-5 shrink-0" />
                  {cat.label}
                </Link>
              ))}
              <Link to="/ambassadors" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-tea-50 hover:text-tea-700 font-medium dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-tea-400 transition-colors">
                Ambassadors
              </Link>
              <Link to="/press" onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-tea-50 hover:text-tea-700 font-medium dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-tea-400 transition-colors">
                Press
              </Link>
              {user && (
                <Link to="/dashboard" onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-tea-50 hover:text-tea-700 font-medium dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-tea-400 transition-colors">
                  Dashboard
                </Link>
              )}
              {user?.role === 'affiliate' && (
                <Link to="/affiliate" onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-tea-50 hover:text-tea-700 font-medium dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-tea-400 transition-colors">
                  Affiliate
                </Link>
              )}
              {user?.role === 'admin' && (
                <>
                  <Link to="/admin/users" onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-tea-50 hover:text-tea-700 font-medium dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-tea-400 transition-colors">
                    Users
                  </Link>
                  <Link to="/admin" onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-tea-50 hover:text-tea-700 font-medium dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-tea-400 transition-colors">
                    Admin
                  </Link>
                </>
              )}

              {user?.walletBalanceCents > 0 && (
                <Link to="/account" onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg bg-tea-50 text-tea-700 font-semibold text-sm dark:bg-tea-900/30 dark:text-tea-400 transition-colors">
                  <Icon name="commissions" className="w-4 h-4 shrink-0" />
                  {fmt(user.walletBalanceCents)} referral cash
                </Link>
              )}
            </div>

            {/* Preferences + auth at the bottom */}
            <div className="px-5 py-4 border-t border-gray-100 dark:border-slate-700 space-y-4">
              {/* Dark mode toggle */}
              <button
                onClick={toggleDarkMode}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
              >
                <span>{darkMode ? 'Light mode' : 'Dark mode'}</span>
                {darkMode ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M18.364 18.364l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Auth actions */}
              {user ? (
                <div className="space-y-2">
                  <Link to="/account" onClick={() => setMenuOpen(false)}
                    className="block w-full text-center btn-secondary text-sm py-2.5">
                    My Account{user.tier === 'platinum' && ' ✦'}
                  </Link>
                  <button onClick={handleLogout} className="w-full btn-secondary text-sm py-2.5 text-red-600 border-red-200 hover:bg-red-50">
                    Logout
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Link to="/login" onClick={() => setMenuOpen(false)} className="block w-full text-center btn-secondary text-sm py-2.5">
                    Login
                  </Link>
                  <Link to="/register" onClick={() => setMenuOpen(false)} className="block w-full text-center btn-primary text-sm py-2.5">
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
